import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  Alert,
  Select,
  MenuItem,
  OutlinedInput,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  LocalOffer as DealIcon,
  AccessTime as TimerIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import ProductPickerModal from "../components/ProductPickerModal";

// ===== TIMEZONE CONVERSION HELPERS =====
// Convert local datetime string to ISO UTC, accounting for browser timezone
const convertLocalToUTC = (localDatetimeString) => {
  if (!localDatetimeString) return null;
  const date = new Date(localDatetimeString);
  // JavaScript Date assumes input is local, toISOString() converts to UTC
  return date.toISOString();
};

// Convert UTC back to local time string for display in form
const convertUTCToLocal = (utcDatetimeString) => {
  if (!utcDatetimeString) return "";
  const date = new Date(utcDatetimeString);
  // Convert to local timezone string format for datetime-local input
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Format datetime for display to user (IST)
const formatDateTimeIST = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

function DealOfTheDaySetup() {
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "https://admin-api.printformee.com/api";

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDealDialogOpen, setAddDealDialogOpen] = useState(false);
  const [editDealDialogOpen, setEditDealDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "" });
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerMode, setProductPickerMode] = useState("new"); // "new" or "edit"

  const [newDeal, setNewDeal] = useState({
    title: "Deals of Day",
    description: "Limited Time",
    discount: 10,
    discountType: "percentage",
    products: [],
    startTime: new Date().toISOString().slice(0, 16),
    endTime: new Date(Date.now() + 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16),
    isActive: true,
    featured: true,
    priority: 0,
    image: "",
    bannerColor: "from-purple-400 to-pink-500",
  });

  const bannerColors = [
    "from-purple-400 to-pink-500",
    "from-orange-400 to-red-500",
    "from-green-400 to-blue-500",
    "from-yellow-400 to-orange-500",
    "from-red-400 to-pink-500",
    "from-indigo-400 to-purple-500",
  ];

  // Fetch deals
  useEffect(() => {
    fetchDeals();
    fetchProducts();
  }, []);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        setNotification({
          open: true,
          message: "No authentication token found",
        });
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/deals-of-the-day`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDeals(response.data.deals || []);
    } catch (error) {
      console.error("Error fetching deals:", error);
      setNotification({
        open: true,
        message: "Error loading deals: " + error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/products/public`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const productsData = response.data.data || response.data.products || response.data || [];
      console.log("Deal products loaded:", productsData);
      
      setAvailableProducts(
        productsData.slice(0, 50).map((p) => ({
          id: p._id || p.id,
          name: p.name || p.product_name_en || p.displayName,
          price: p.price || p.product_price || 0,
          image: p.images?.[0]?.url || p.image || p.thumbnail || "/placeholder.png",
        }))
      );
    } catch (error) {
      console.error("Error fetching products:", error);
      setAvailableProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleCreateDeal = async () => {
    try {
      if (!newDeal.title || !newDeal.discount) {
        setNotification({
          open: true,
          message: "Please fill in all required fields",
        });
        return;
      }

      const token = localStorage.getItem("token");
      // Convert local times to UTC before sending to backend
      const dealToSend = {
        ...newDeal,
        startTime: convertLocalToUTC(newDeal.startTime),
        endTime: convertLocalToUTC(newDeal.endTime),
      };
      const response = await axios.post(
        `${API_BASE_URL}/deals-of-the-day`,
        dealToSend,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setDeals([...deals, response.data.deal]);
      setAddDealDialogOpen(false);
      setNewDeal({
        title: "Deals of Day",
        description: "Limited Time",
        discount: 10,
        discountType: "percentage",
        products: [],
        startTime: new Date().toISOString().slice(0, 16),
        endTime: new Date(Date.now() + 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 16),
        isActive: true,
        featured: true,
        priority: 0,
        image: "",
        bannerColor: "from-purple-400 to-pink-500",
      });
      setNotification({
        open: true,
        message: "Deal created successfully!",
      });
    } catch (error) {
      console.error("Error creating deal:", error);
      setNotification({
        open: true,
        message: "Error creating deal: " + error.message,
      });
    }
  };

  const handleUpdateDeal = async () => {
    try {
      if (!editingDeal.title || !editingDeal.discount) {
        setNotification({
          open: true,
          message: "Please fill in all required fields",
        });
        return;
      }

      const token = localStorage.getItem("token");
      // Convert local times to UTC before sending to backend
      const dealToSend = {
        ...editingDeal,
        startTime: convertLocalToUTC(editingDeal.startTime),
        endTime: convertLocalToUTC(editingDeal.endTime),
      };
      const response = await axios.put(
        `${API_BASE_URL}/deals-of-the-day/${editingDeal._id}`,
        dealToSend,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setDeals(
        deals.map((d) => (d._id === editingDeal._id ? response.data.deal : d))
      );
      setEditDealDialogOpen(false);
      setEditingDeal(null);
      setNotification({
        open: true,
        message: "Deal updated successfully!",
      });
    } catch (error) {
      console.error("Error updating deal:", error);
      setNotification({
        open: true,
        message: "Error updating deal: " + error.message,
      });
    }
  };

  const handleDeleteDeal = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/deals-of-the-day/${selectedDeal._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDeals(deals.filter((d) => d._id !== selectedDeal._id));
      setDeleteDialogOpen(false);
      setSelectedDeal(null);
      setNotification({
        open: true,
        message: "Deal deleted successfully!",
      });
    } catch (error) {
      console.error("Error deleting deal:", error);
      setNotification({
        open: true,
        message: "Error deleting deal: " + error.message,
      });
    }
  };

  const handleToggleActive = async (deal) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${API_BASE_URL}/deals-of-the-day/${deal._id}/toggle`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setDeals(
        deals.map((d) => (d._id === deal._id ? response.data.deal : d))
      );
      setNotification({
        open: true,
        message: `Deal ${response.data.deal.isActive ? "activated" : "deactivated"}!`,
      });
    } catch (error) {
      console.error("Error toggling deal status:", error);
      setNotification({
        open: true,
        message: "Error toggling status: " + error.message,
      });
    }
  };

  const calculateTimeRemaining = (deal) => {
    const now = new Date();
    const end = new Date(deal.endTime);
    const diff = end - now;

    if (diff <= 0) return "Expired";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d left`;
    }
    return `${hours}h ${minutes}m left`;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Box className="p-6">
      <Box className="flex justify-between items-center mb-6">
        <Typography variant="h4" className="flex items-center gap-2">
          <DealIcon className="text-orange-500" />
          Deals of the Day
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setAddDealDialogOpen(true)}
        >
          Add New Deal
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading deals...</Typography>
      ) : deals.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">No deals created yet. Create your first deal!</Alert>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead className="bg-gray-100">
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell align="center">Discount</TableCell>
                <TableCell align="center">Products</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Time Remaining</TableCell>
                <TableCell align="center">Start - End</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deals.map((deal) => (
                <TableRow key={deal._id} hover>
                  <TableCell>
                    <Typography variant="body2" className="font-semibold">
                      {deal.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {deal.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${deal.discount}${deal.discountType === "percentage" ? "%" : "$"}`}
                      color={deal.isActive ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {deal.products?.length || 0} products
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={deal.isActive}
                          onChange={() => handleToggleActive(deal)}
                        />
                      }
                      label={deal.isActive ? "Active" : "Inactive"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={formatDateTime(deal.endTime)}>
                      <Typography variant="body2" className="flex items-center gap-1 justify-center">
                        <TimerIcon size={16} />
                        {calculateTimeRemaining(deal)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">
                      {formatDateTimeIST(deal.startTime)}
                      {" → "}
                      {formatDateTimeIST(deal.endTime)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setEditingDeal({
                          ...deal,
                          startTime: convertUTCToLocal(deal.startTime),
                          endTime: convertUTCToLocal(deal.endTime),
                        });
                        setEditDealDialogOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setSelectedDeal(deal);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Deal Dialog */}
      <Dialog
        open={addDealDialogOpen}
        onClose={() => setAddDealDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Deal</DialogTitle>
        <DialogContent className="space-y-4 mt-4">
          <TextField
            fullWidth
            label="Title"
            value={newDeal.title}
            onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            value={newDeal.description}
            onChange={(e) =>
              setNewDeal({ ...newDeal, description: e.target.value })
            }
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Discount Value"
                value={newDeal.discount || ""}
                onChange={(e) =>
                  setNewDeal({ ...newDeal, discount: parseInt(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Discount Type"
                value={newDeal.discountType}
                onChange={(e) =>
                  setNewDeal({ ...newDeal, discountType: e.target.value })
                }
                SelectProps={{
                  native: true,
                }}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed ($)</option>
              </TextField>
            </Grid>
          </Grid>

          {/* Product Selection - Modal Picker */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Select Products
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setProductPickerMode("new");
                setProductPickerOpen(true);
              }}
              sx={{ mb: 1.5 }}
            >
              + Add Products ({newDeal.products.length} selected)
            </Button>

            {/* Display Selected Products as Chips */}
            {newDeal.products.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {newDeal.products.map((productId) => {
                  const product = availableProducts.find((p) => p.id === productId);
                  return (
                    <Chip
                      key={productId}
                      avatar={
                        product ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src = "/placeholder.png";
                            }}
                          />
                        ) : undefined
                      }
                      label={product ? product.name : "Unknown"}
                      onDelete={() =>
                        setNewDeal({
                          ...newDeal,
                          products: newDeal.products.filter(
                            (id) => id !== productId
                          ),
                        })
                      }
                      sx={{ maxWidth: "100%" }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Start Time (IST)"
                value={newDeal.startTime}
                onChange={(e) =>
                  setNewDeal({ ...newDeal, startTime: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                helperText="Indian Standard Time (IST)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="End Time (IST)"
                value={newDeal.endTime}
                onChange={(e) =>
                  setNewDeal({ ...newDeal, endTime: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                helperText="Indian Standard Time (IST)"
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            select
            label="Banner Color"
            value={newDeal.bannerColor}
            onChange={(e) =>
              setNewDeal({ ...newDeal, bannerColor: e.target.value })
            }
            SelectProps={{
              native: true,
            }}
          >
            {bannerColors.map((color) => (
              <option key={color} value={color}>
                {color}
              </option>
            ))}
          </TextField>
          <TextField
            fullWidth
            type="number"
            label="Priority"
            value={newDeal.priority || 0}
            onChange={(e) =>
              setNewDeal({ ...newDeal, priority: parseInt(e.target.value) || 0 })
            }
            helperText="Higher number = higher priority"
          />
          <FormControlLabel
            control={
              <Switch
                checked={newDeal.isActive}
                onChange={(e) =>
                  setNewDeal({ ...newDeal, isActive: e.target.checked })
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDealDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateDeal} variant="contained" color="primary">
            Create Deal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Deal Dialog */}
      <Dialog
        open={editDealDialogOpen}
        onClose={() => setEditDealDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Deal</DialogTitle>
        {editingDeal && (
          <DialogContent className="space-y-4 mt-4">
            <TextField
              fullWidth
              label="Title"
              value={editingDeal.title}
              onChange={(e) =>
                setEditingDeal({ ...editingDeal, title: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Description"
              value={editingDeal.description}
              onChange={(e) =>
                setEditingDeal({ ...editingDeal, description: e.target.value })
              }
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount Value"
                  value={editingDeal.discount || ""}
                  onChange={(e) =>
                    setEditingDeal({
                      ...editingDeal,
                      discount: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Discount Type"
                  value={editingDeal.discountType}
                  onChange={(e) =>
                    setEditingDeal({
                      ...editingDeal,
                      discountType: e.target.value,
                    })
                  }
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed ($)</option>
                </TextField>
              </Grid>
            </Grid>

            {/* Product Selection - Modal Picker */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Select Products
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setProductPickerMode("edit");
                  setProductPickerOpen(true);
                }}
                sx={{ mb: 1.5 }}
              >
                + Add Products ({(editingDeal?.products || []).length} selected)
              </Button>

              {/* Display Selected Products as Chips */}
              {(editingDeal?.products || []).length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {(editingDeal?.products || []).map((productId) => {
                    const product = availableProducts.find((p) => p.id === productId);
                    return (
                      <Chip
                        key={productId}
                        avatar={
                          product ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.src = "/placeholder.png";
                              }}
                            />
                          ) : undefined
                        }
                        label={product ? product.name : "Unknown"}
                        onDelete={() =>
                          setEditingDeal({
                            ...editingDeal,
                            products: (editingDeal.products || []).filter(
                              (id) => id !== productId
                            ),
                          })
                        }
                        sx={{ maxWidth: "100%" }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start Time (IST)"
                  value={editingDeal.startTime || ""}
                  onChange={(e) =>
                    setEditingDeal({ ...editingDeal, startTime: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  helperText="Indian Standard Time (IST)"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="End Time (IST)"
                  value={editingDeal.endTime || ""}
                  onChange={(e) =>
                    setEditingDeal({ ...editingDeal, endTime: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  helperText="Indian Standard Time (IST)"
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              select
              label="Banner Color"
              value={editingDeal.bannerColor}
              onChange={(e) =>
                setEditingDeal({ ...editingDeal, bannerColor: e.target.value })
              }
              SelectProps={{
                native: true,
              }}
            >
              {bannerColors.map((color) => (
                <option key={color} value={color}>
                  {color}
                </option>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="number"
              label="Priority"
              value={editingDeal.priority || 0}
              onChange={(e) =>
                setEditingDeal({
                  ...editingDeal,
                  priority: parseInt(e.target.value) || 0,
                })
              }
              helperText="Higher number = higher priority"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingDeal.isActive}
                  onChange={(e) =>
                    setEditingDeal({
                      ...editingDeal,
                      isActive: e.target.checked,
                    })
                  }
                />
              }
              label="Active"
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditDealDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateDeal} variant="contained" color="primary">
            Update Deal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this deal? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteDeal} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      {notification.open && (
        <Box className="fixed bottom-4 right-4 bg-blue-500 text-white p-4 rounded shadow-lg flex items-center gap-2">
          {notification.message}
          <IconButton
            size="small"
            onClick={() => setNotification({ ...notification, open: false })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Product Picker Modal */}
      <ProductPickerModal
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        selectedProductIds={
          productPickerMode === "new"
            ? newDeal.products
            : editingDeal?.products || []
        }
        onSelect={(selectedIds) => {
          if (productPickerMode === "new") {
            setNewDeal({ ...newDeal, products: selectedIds });
          } else {
            setEditingDeal({ ...editingDeal, products: selectedIds });
          }
        }}
      />
    </Box>
  );
}

export default DealOfTheDaySetup;
